import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { clsx } from 'clsx';
import { useEffect, useState, type ReactNode, type RefObject } from 'react';

interface TooltipProps {
    title: ReactNode;
    children: ReactNode;
    className?: string;
    labelClassName?: string;
    variant?: 'default' | 'teal';
    triggerRef?: RefObject<HTMLElement | HTMLButtonElement | null>;
    show?: boolean;
    onOpenChange?: (open: boolean) => void;
}

const Tooltip = ({
    className,
    title,
    children,
    labelClassName,
    variant = 'default',
    triggerRef,
    show = false,
    onOpenChange,
}: TooltipProps) => {
    const [position, setPosition] = useState<{ top: string; left: string } | null>(null);

    // Update tooltip position for fixed positioning
    useEffect(() => {
        const updatePosition = () => {
            if (triggerRef?.current && show) {
                const rect = triggerRef.current.getBoundingClientRect();
                setPosition({
                    top: `${rect.top + window.scrollY - 12}px`,
                    left: `${rect.left + window.scrollX + rect.width / 2}px`,
                });
            }
        };

        updatePosition();
        if (show) {
            window.addEventListener('scroll', updatePosition);
            window.addEventListener('resize', updatePosition);
            return () => {
                window.removeEventListener('scroll', updatePosition);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [show, triggerRef]);

    // If using fixed positioning with triggerRef
    if (triggerRef && onOpenChange) {
        const tealClasses =
            variant === 'teal'
                ? 'rounded-md border-none bg-teal-50 px-5 py-5 text-center font-medium text-neutral-500'
                : 'rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-700';

        return (
            <>
                <div
                    className={labelClassName}
                    onMouseEnter={() => onOpenChange(true)}
                    onMouseLeave={() => onOpenChange(false)}
                >
                    {children}
                </div>

                {/* Fixed positioning tooltip */}
                {show && position && (
                    <div
                        className={clsx('fixed z-50 w-max max-w-80 shadow-lg', tealClasses)}
                        style={{
                            top: position.top,
                            left: position.left,
                            transform: 'translate(-70%, -100%)',
                        }}
                    >
                        <p className={variant === 'teal' ? 'text-base leading-6' : 'normal-case'}>{title}</p>
                    </div>
                )}
            </>
        );
    }

    // Default popover-based behavior
    return (
        <Popover className={clsx('relative', className)}>
            <PopoverButton as="div" className={clsx('cursor-pointer', labelClassName)}>
                {children}
            </PopoverButton>
            <PopoverPanel
                anchor={{ to: 'top', gap: '8px', padding: '16px' }}
                transition
                className="z-50 w-max max-w-80 rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-700 normal-case shadow-lg transition duration-200 ease-out data-[closed]:translate-y-1 data-[closed]:opacity-0"
            >
                {title}
            </PopoverPanel>
        </Popover>
    );
};

export default Tooltip;
